'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { type Coordinates, type GoogleLocationResult, parseGoogleMapsInput, type PastedLocationResult, useNearbyBias, usePastedGoogleMapsLocation, useSearchLocation } from '@repo/geolocation';
import { CompanyLocation } from '@repo/schema/location';
import { useQuery } from '@repo/tanstack/query';
import { Button, Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, Popover, PopoverContent, PopoverTrigger, returnFocusUnlessMoved, Skeleton, Spinner } from '@repo/ui/components';
import { mergeRefs } from '@repo/ui/lib/merge-refs';
import { cn } from '@repo/ui/lib/utils';
import { Check, ChevronDown, MapPin, Plus } from '@repo/ui/icons';
import { type Ref, useEffect, useRef, useState } from 'react';
import { GoogleLocationRow } from './google-location-row.tsx';
import { LocationAddressDisplay } from './location-address-display.tsx';
export type LocationPlaceValue = {
  address?: null | string;
  address_id?: null | number;
  address_second_line?: null | string;
  city?: null | string;
  country?: string;
  country_code?: null | string;
  lat?: null | string;
  lng?: null | string;
  location_id?: null | number;
  name?: null | string;
  place_id?: null | string;
  place_name?: null | string;
  postcode?: null | string;
  uk_region?: null | string;
};
type CompanyLocationSearchProps = {
  className?: string;
  companyId?: null | number;
  disabled?: boolean;
  invalid?: boolean;
  isCreatingLocation?: boolean;
  /** Nearby bias reference point used when the device location is unavailable. */
  nearbyCoordinates?: Coordinates;
  onAddNewLocation: (context: {
    googlePlace?: GoogleLocationResult;
    query: string;
  }) => void;
  onCompanyLocationSelect: (location: CompanyLocation) => void;
  onGoogleLocationSelect: (location: GoogleLocationResult) => void;
  onPastedLocationSelect?: (location: PastedLocationResult) => void;
  orgIdOverride?: number;
  placeholder?: string;
  restoreFocusOnSelect?: boolean;
  triggerRef?: Ref<HTMLButtonElement>;
  triggerVariant?: 'ghost' | 'outline';
  value?: LocationPlaceValue | null;
};
const CompanyLocationsSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="space-y-2 p-2" data-sentry-component="CompanyLocationsSkeleton" data-sentry-source-file="company-location-search.tsx">{Array.from({
        length: 3
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const CompanyLocationSearch = t0 => {
  const $ = _c(101);
  const {
    className,
    companyId,
    disabled,
    invalid,
    isCreatingLocation,
    nearbyCoordinates,
    onAddNewLocation,
    onCompanyLocationSelect,
    onGoogleLocationSelect,
    onPastedLocationSelect,
    orgIdOverride,
    placeholder: t1,
    restoreFocusOnSelect: t2,
    triggerRef,
    triggerVariant: t3,
    value
  } = t0;
  const placeholder = t1 === undefined ? "Select location" : t1;
  const restoreFocusOnSelect = t2 === undefined ? false : t2;
  const triggerVariant = t3 === undefined ? "outline" : t3;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debounceRef = useRef(null);
  const internalTriggerRef = useRef(null);
  const popupRef = useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const clearDebouncedSearch = t4;
  let t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => () => clearDebouncedSearch();
    t6 = [];
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  useEffect(t5, t6);
  const displayLabel = value?.name ?? value?.place_name ?? "";
  let t7;
  if ($[3] !== disabled || $[4] !== query) {
    t7 = {
      disabled,
      query
    };
    $[3] = disabled;
    $[4] = query;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const {
    isLoading: isPastedLocationLoading,
    isPastedInput,
    mapsLabel,
    pastedLocation
  } = usePastedGoogleMapsLocation(t7);
  let t8;
  if ($[6] !== nearbyCoordinates || $[7] !== open) {
    t8 = {
      enabled: open,
      stopCoordinates: nearbyCoordinates
    };
    $[6] = nearbyCoordinates;
    $[7] = open;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const nearbyBias = useNearbyBias(t8);
  const t9 = !disabled && !isPastedInput;
  let t10;
  if ($[9] !== nearbyBias || $[10] !== searchQuery || $[11] !== t9) {
    t10 = {
      bias: nearbyBias,
      enabled: t9,
      query: searchQuery
    };
    $[9] = nearbyBias;
    $[10] = searchQuery;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const {
    data: googleResults,
    isFetching: isGoogleFetching
  } = useSearchLocation(t10);
  const api = useApi();
  let t11;
  if ($[13] !== query) {
    t11 = query.trim();
    $[13] = query;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const trimmedQuery = t11;
  let t12;
  if ($[15] !== api.companies.locations.list || $[16] !== companyId || $[17] !== disabled || $[18] !== orgIdOverride || $[19] !== searchQuery) {
    t12 = api.companies.locations.list.queryOptions({
      input: {
        org_id: orgIdOverride,
        page: 1,
        perPage: 100,
        ...(searchQuery ? {
          search: searchQuery
        } : {})
      },
      path: {
        id: companyId ?? 0
      }
    }, {
      enabled: !!companyId && !disabled
    });
    $[15] = api.companies.locations.list;
    $[16] = companyId;
    $[17] = disabled;
    $[18] = orgIdOverride;
    $[19] = searchQuery;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const {
    data: companyLocations,
    isLoading: isCompanyLocationsLoading
  } = useQuery(t12);
  const hasCompanyLocations = (companyLocations?.data?.length ?? 0) > 0;
  const hasGoogleResults = (googleResults?.length ?? 0) > 0;
  const isLoading = isGoogleFetching || isCompanyLocationsLoading || isPastedLocationLoading;
  const showEmpty = !isPastedInput && !isLoading && !hasCompanyLocations && !hasGoogleResults && searchQuery === trimmedQuery && trimmedQuery.length > 2;
  const showManualCreate = showEmpty || isPastedInput && !isLoading && pastedLocation === null;
  let t13;
  if ($[21] !== value) {
    t13 = locationId => value?.location_id != null && value.location_id === locationId;
    $[21] = value;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const isCompanyLocationSelected = t13;
  const pastedLocationKey = pastedLocation?.kind === "google-location" ? pastedLocation.googlePlace.place_id : pastedLocation && `${pastedLocation.coordinates.lat}-${pastedLocation.coordinates.lng}`;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = open_0 => {
      setOpen(open_0);
      if (!open_0) {
        clearDebouncedSearch();
        setQuery("");
        setSearchQuery("");
      }
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = !displayLabel && "text-muted-foreground";
  let t16;
  if ($[24] !== className || $[25] !== t15) {
    t16 = cn("w-full min-w-0 justify-between gap-1.5", t15, className);
    $[24] = className;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== triggerRef) {
    t17 = mergeRefs(internalTriggerRef, triggerRef);
    $[27] = triggerRef;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = triggerVariant === "ghost" ? 1 : "default";
  let t19;
  if ($[29] !== triggerVariant) {
    t19 = triggerVariant !== "ghost" && <MapPin className="size-4 shrink-0" />;
    $[29] = triggerVariant;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  const t20 = displayLabel || placeholder;
  let t21;
  if ($[31] !== t20) {
    t21 = <span className="min-w-0 truncate">{t20}</span>;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t19 || $[34] !== t21) {
    t22 = <span className="flex min-w-0 flex-1 items-center gap-1.5">{t19}{t21}</span>;
    $[33] = t19;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== isCreatingLocation || $[37] !== triggerVariant) {
    t23 = isCreatingLocation ? <Spinner className="size-4 shrink-0" /> : triggerVariant !== "ghost" && <ChevronDown className="size-4 shrink-0 text-muted-foreground" />;
    $[36] = isCreatingLocation;
    $[37] = triggerVariant;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== disabled || $[40] !== invalid || $[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t22 || $[45] !== t23 || $[46] !== triggerVariant) {
    t24 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="company-location-search.tsx"><Button aria-invalid={invalid} className={t16} disabled={disabled} ref={t17} size={t18} type="button" variant={triggerVariant} data-sentry-element="Button" data-sentry-source-file="company-location-search.tsx">{t22}{t23}</Button></PopoverTrigger>;
    $[39] = disabled;
    $[40] = invalid;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t22;
    $[45] = t23;
    $[46] = triggerVariant;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== restoreFocusOnSelect) {
    t25 = restoreFocusOnSelect ? () => returnFocusUnlessMoved(internalTriggerRef, popupRef)() : undefined;
    $[48] = restoreFocusOnSelect;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = value_0 => {
      setQuery(value_0);
      clearDebouncedSearch();
      if (parseGoogleMapsInput(value_0)) {
        setSearchQuery("");
        return;
      }
      debounceRef.current = setTimeout(() => {
        setSearchQuery(value_0.trim());
      }, 300);
    };
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== disabled || $[52] !== query) {
    t27 = <CommandInput disabled={disabled} onValueChange={t26} placeholder="Search locations..." value={query} data-sentry-element="CommandInput" data-sentry-source-file="company-location-search.tsx" />;
    $[51] = disabled;
    $[52] = query;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== isLoading) {
    t28 = isLoading && <CompanyLocationsSkeleton />;
    $[54] = isLoading;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== companyLocations?.data || $[57] !== hasCompanyLocations || $[58] !== isCompanyLocationSelected || $[59] !== isLoading || $[60] !== isPastedInput || $[61] !== onCompanyLocationSelect) {
    t29 = !isPastedInput && !isLoading && hasCompanyLocations && <CommandGroup heading="Company locations">{companyLocations?.data?.map(location => {
        const selected = isCompanyLocationSelected(location.id);
        return <CommandItem aria-selected={selected} className={cn("flex w-full min-w-0 items-start justify-between gap-2", selected && "bg-accent")} key={location.id} onSelect={() => {
          onCompanyLocationSelect(location);
          setOpen(false);
          clearDebouncedSearch();
          setQuery("");
          setSearchQuery("");
        }} value={`company-${location.id}-${location.name}`}><div className="flex min-w-0 flex-1 flex-col gap-0.5"><span className="truncate font-medium">{location.name}</span><LocationAddressDisplay className="block truncate text-xs text-muted-foreground [&_*]:truncate" value={{
              address: location.address?.address,
              address_second_line: location.address?.address_second_line,
              city: location.address?.city,
              country_code: location.address?.country_code,
              postcode: location.address?.postcode
            }} /></div>{selected && <Check className="size-4 shrink-0 self-center text-primary" />}</CommandItem>;
      })}</CommandGroup>;
    $[56] = companyLocations?.data;
    $[57] = hasCompanyLocations;
    $[58] = isCompanyLocationSelected;
    $[59] = isLoading;
    $[60] = isPastedInput;
    $[61] = onCompanyLocationSelect;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== hasCompanyLocations || $[64] !== hasGoogleResults || $[65] !== isLoading || $[66] !== isPastedInput) {
    t30 = !isPastedInput && !isLoading && hasCompanyLocations && hasGoogleResults && <CommandSeparator />;
    $[63] = hasCompanyLocations;
    $[64] = hasGoogleResults;
    $[65] = isLoading;
    $[66] = isPastedInput;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== googleResults || $[69] !== hasGoogleResults || $[70] !== isLoading || $[71] !== isPastedInput || $[72] !== onGoogleLocationSelect) {
    t31 = !isPastedInput && !isLoading && hasGoogleResults && <CommandGroup heading="Google locations">{googleResults?.map(result => <CommandItem className="w-full min-w-0 items-start py-2" key={result.place_id} onSelect={() => {
        onGoogleLocationSelect(result);
        setOpen(false);
        clearDebouncedSearch();
        setQuery("");
        setSearchQuery("");
      }} value={`google-${result.place_id}-${result.place_name}`}><GoogleLocationRow place={result} /></CommandItem>)}</CommandGroup>;
    $[68] = googleResults;
    $[69] = hasGoogleResults;
    $[70] = isLoading;
    $[71] = isPastedInput;
    $[72] = onGoogleLocationSelect;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== isLoading || $[75] !== onPastedLocationSelect || $[76] !== pastedLocation || $[77] !== pastedLocationKey) {
    t32 = !isLoading && pastedLocation && <CommandGroup heading={pastedLocation.kind === "google-location" ? "Google locations" : "Pinned coordinates"}><CommandItem className="w-full min-w-0 items-start gap-2 py-2 [&>svg:last-child]:hidden" onSelect={() => {
        onPastedLocationSelect?.(pastedLocation);
        setOpen(false);
        clearDebouncedSearch();
        setQuery("");
        setSearchQuery("");
      }} value={`pasted-${pastedLocationKey}`}>{pastedLocation.kind === "google-location" ? <GoogleLocationRow hint="Creates a new Company location" place={pastedLocation.googlePlace} /> : <><MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" /><div className="min-w-0 flex-1"><span className="block truncate">{`${pastedLocation.coordinates.lat}, ${pastedLocation.coordinates.lng}`}</span><span className="block text-xs text-muted-foreground">Creates a new Company location</span></div></>}</CommandItem></CommandGroup>;
    $[74] = isLoading;
    $[75] = onPastedLocationSelect;
    $[76] = pastedLocation;
    $[77] = pastedLocationKey;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== mapsLabel || $[80] !== onAddNewLocation || $[81] !== query || $[82] !== showManualCreate) {
    t33 = showManualCreate && <div className="flex flex-col gap-2 p-2"><Button className="justify-start gap-2" onClick={() => {
        clearDebouncedSearch();
        onAddNewLocation({
          query: mapsLabel ?? query
        });
      }} type="button" variant="ghost"><Plus className="size-4" />Add new location</Button></div>;
    $[79] = mapsLabel;
    $[80] = onAddNewLocation;
    $[81] = query;
    $[82] = showManualCreate;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== t28 || $[85] !== t29 || $[86] !== t30 || $[87] !== t31 || $[88] !== t32 || $[89] !== t33) {
    t34 = <CommandList className="max-h-72" data-sentry-element="CommandList" data-sentry-source-file="company-location-search.tsx">{t28}{t29}{t30}{t31}{t32}{t33}</CommandList>;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
    $[87] = t31;
    $[88] = t32;
    $[89] = t33;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== t27 || $[92] !== t34) {
    t35 = <Command shouldFilter={false} data-sentry-element="Command" data-sentry-source-file="company-location-search.tsx">{t27}{t34}</Command>;
    $[91] = t27;
    $[92] = t34;
    $[93] = t35;
  } else {
    t35 = $[93];
  }
  let t36;
  if ($[94] !== t25 || $[95] !== t35) {
    t36 = <PopoverContent align="start" className="w-80 max-w-[calc(100vw-2rem)] p-0" collisionPadding={10} finalFocus={t25} ref={popupRef} side="bottom" data-sentry-element="PopoverContent" data-sentry-source-file="company-location-search.tsx">{t35}</PopoverContent>;
    $[94] = t25;
    $[95] = t35;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  let t37;
  if ($[97] !== open || $[98] !== t24 || $[99] !== t36) {
    t37 = <Popover onOpenChange={t14} open={open} data-sentry-element="Popover" data-sentry-component="CompanyLocationSearch" data-sentry-source-file="company-location-search.tsx">{t24}{t36}</Popover>;
    $[97] = open;
    $[98] = t24;
    $[99] = t36;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  return t37;
};
function _temp(_, i) {
  return <Skeleton className="h-10 w-full" key={i} />;
}